import {
  evaluateConditions,
  getInstanceCount,
  scopeValuesToInstance,
  sectionMinInstances,
} from '@bmo/form-core';

import type { FormSection, SourceFieldRef } from '@/api/workflows';

import type { FieldValue } from './FieldRenderer';

/**
 * Which instance of a cross-step prefill's source to copy from (RDSP QA D-15 / D-22: Caregiver 1
 * is filled from WHICHEVER account holder said they are the primary caregiver).
 *
 * - No conditions: instance 0, unconditionally — the behaviour before prefills could be gated.
 * - Source in a repeatable section: each instance is tried in order with the values scoped to it
 *   (`scopeValuesToInstance` with the whole workflow, so a condition on a section that repeats
 *   once per instance of the source — the per-holder relationship block — reads that same
 *   holder). The FIRST instance that passes wins.
 * - Source anywhere else: the conditions are evaluated once, as they stand.
 *
 * Null means no instance passes, and the caller copies nothing.
 */
export function resolvePrefillSourceInstance(
  ref: SourceFieldRef,
  sourceSection: FormSection | undefined,
  values: Record<string, FieldValue>,
  allSections: readonly FormSection[],
): number | null {
  const conditions = ref.conditions ?? [];
  if (conditions.length === 0) return 0;
  const combinator = ref.conditionCombinator ?? null;
  if (!sourceSection?.repeatable) {
    return evaluateConditions(conditions, combinator, values) ? 0 : null;
  }
  const count = getInstanceCount(values, sourceSection.id, sectionMinInstances(sourceSection));
  for (let k = 0; k < count; k++) {
    const scoped = scopeValuesToInstance(sourceSection, values, k, allSections);
    if (evaluateConditions(conditions, combinator, scoped)) return k;
  }
  return null;
}
